/**
 * useAuth hook — read access to the AuthContext value.
 *
 * Lives separately from `AuthContext.tsx` because Vite's react-refresh
 * plugin requires component modules to export ONLY components. Mixing a
 * hook export alongside `AuthProvider` defeats fast refresh on every
 * file that imports auth state, which is essentially the entire app.
 */
import { AuthContext, type AuthContextValue } from '@redirector/front/context/authContextValue';
import type { User } from '@redirector/front/types/types';
import { useContext } from 'react';

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * The signed-in user, for components rendered under `RequireAuth` (every
 * protected page), which renders its children only once a user is loaded.
 */
export function useCurrentUser(): User {
  const { user } = useAuth();
  if (!user) {
    throw new Error('useCurrentUser must be used under RequireAuth');
  }
  return user;
}
